import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { VacationAvatar } from './VacationAvatar';
import { VacationDpPeriodCell } from './VacationDpPeriodCell';
import {
    getCollaboratorProfileHref,
    getRequestStatusMetadata,
    getReceiptPendingLabel,
} from '../../../vacations.definitions';
import { resolveDpDeadline } from '../../../utils/vacationDpDeadline';
import { resolveDpDays } from '../../../utils/vacationDpDays';
import { resolveApprovedPeriodCells } from '../../../utils/vacationDpPeriods';
import type { VacationDpRow } from '../../../models/vacationDpBoard.types';
import {
    CARD_FIELD_LABELS,
    COLUMN_LABELS_BY_PROFILE,
    CELL_TONE_CLASSES,
    NOT_APPLICABLE,
    NO_CONFLICT,
    NO_REQUEST_ACTION_TITLE,
    NO_REQUEST_LABEL,
    OPEN_REQUEST_LABEL,
    SELL_SHORT_LABELS,
    UNALLOCATED_WARNING_LABEL,
} from '../vacationDpTable.definitions';
import type { CellTone } from '../vacationDpTable.types';
import type { DpColumnProfile } from '../vacationDpTable.definitions';
export interface VacationDpCardProps {
    row: VacationDpRow;
    onOpenRequest: (row: VacationDpRow) => void;
    onOpenHistory?: (collaborator: { id: string; name: string; email: string }) => void;
    today: Date;
    profile?: DpColumnProfile;
}
function ToneBadge({ tone, children }: { tone: CellTone; children: React.ReactNode }) {
    if (tone === 'none') {
        return <span className="font-montserrat text-raro-gray-800">{children}</span>;
    }
    if (tone === 'muted') {
        return <span className="font-montserrat text-raro-gray-400">{children}</span>;
    }
    return (
        <span
            className={clsx(
                'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium font-montserrat',
                CELL_TONE_CLASSES[tone]
            )}
        >
            {children}
        </span>
    );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-raro-gray-500 font-montserrat">{label}</span>
            <span className="text-right">{children}</span>
        </div>
    );
}
export function VacationDpCard({ row, onOpenRequest, onOpenHistory, today, profile = 'general' }: VacationDpCardProps) {
    const deadlineInfo = resolveDpDeadline(row, today);
    const stage = row.request ? getRequestStatusMetadata(row.request) : null;
    const receiptPendingLabel = row.request ? getReceiptPendingLabel(row.request) : null;
    const sell = row.request?.sellRequest;
    const conflicts = row.request?.conflictsSnapshot ?? [];
    const profileLabel = COLUMN_LABELS_BY_PROFILE[profile].days;
    const daysLabel = row.request ? 'Solicitado' : profileLabel;
    const conflictLabel = (() => {
        if (!row.request) return null;
        if (conflicts.length === 0) return NO_CONFLICT;
        const first = conflicts[0];
        const name = first.projectName ?? first.clientName ?? 'Alocação ativa';
        return conflicts.length > 1 ? `${name} +${conflicts.length - 1}` : name;
    })();
    return (
        <article className="rounded-2xl border border-raro-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    <VacationAvatar
                        name={row.collaboratorName}
                        photoUrl={row.collaboratorPhotoUrl}
                    />
                    <Link
                        to={getCollaboratorProfileHref(row.collaboratorId)}
                        className="truncate font-medium text-raro-gray-900 underline-offset-2 transition-colors hover:text-primary-700 hover:underline font-montserrat"
                    >
                        {row.collaboratorName}
                    </Link>
                </div>
                {stage ? (
                    <div className="flex shrink-0 items-center gap-1">
                        <span
                            className={clsx(
                                'rounded-md px-2 py-1 text-xs font-medium font-montserrat',
                                stage.className
                            )}
                        >
                            {stage.label}
                        </span>
                        {receiptPendingLabel && (
                            <span className="rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 font-montserrat">
                                {receiptPendingLabel}
                            </span>
                        )}
                    </div>
                ) : (
                    <span className="shrink-0 text-xs text-raro-gray-400 font-montserrat">
                        {NO_REQUEST_LABEL}
                    </span>
                )}
            </div>
            <div className="mt-3 space-y-0.5">
                <ToneBadge tone={deadlineInfo.tone as CellTone}>
                    {deadlineInfo.label}
                </ToneBadge>
                {deadlineInfo.hint && (
                    <span className="block text-xs text-raro-gray-500 font-montserrat">
                        {deadlineInfo.hint}
                    </span>
                )}
            </div>
            <div className="mt-3 space-y-1.5 border-t border-raro-gray-100 pt-3">
                {(() => {
                    const daysInfo = resolveDpDays(row);
                    const isExpired = daysInfo.kind === 'expired';
                    const valueText = isExpired ? `${daysInfo.value}d vencidos` : `${daysInfo.value}d`;
                    return (
                        <>
                            <Field label={daysLabel}>
                                <span className={clsx('inline-flex items-center justify-end gap-1', isExpired && 'text-red-700 font-semibold')}>
                                    <span className="tabular-nums font-medium font-montserrat">
                                        {valueText}
                                    </span>
                                    {row.unallocatedDays > 0 && (
                                        <div title={UNALLOCATED_WARNING_LABEL}>
                                            <ExclamationTriangleIcon
                                                className="h-4 w-4 text-amber-500"
                                                aria-hidden="true"
                                            />
                                            <span className="sr-only">{UNALLOCATED_WARNING_LABEL}</span>
                                        </div>
                                    )}
                                </span>
                            </Field>
                            {daysInfo.hint && (
                                <div className="text-right tabular-nums text-xs text-raro-gray-500 font-montserrat">
                                    {daysInfo.hint}
                                </div>
                            )}
                        </>
                    );
                })()}
                {profile === 'periods' && (() => {
                    const [period1, period2] = resolveApprovedPeriodCells(row, today);
                    return (
                        <>
                            <Field label="1º período">
                                <VacationDpPeriodCell cell={period1} />
                            </Field>
                            {period2 && (
                                <Field label="2º período">
                                    <VacationDpPeriodCell cell={period2} />
                                </Field>
                            )}
                        </>
                    );
                })()}
                <Field label={CARD_FIELD_LABELS.sell}>
                    {sell?.requested ? (
                        <ToneBadge tone={sell.status === 'Pending' ? 'primary' : 'none'}>
                            {SELL_SHORT_LABELS[sell.status] ?? sell.status}
                        </ToneBadge>
                    ) : (
                        <ToneBadge tone="muted">{NOT_APPLICABLE}</ToneBadge>
                    )}
                </Field>
                <Field label={CARD_FIELD_LABELS.conflict}>
                    {conflictLabel === null ? (
                        <ToneBadge tone="muted">{NOT_APPLICABLE}</ToneBadge>
                    ) : (
                        <ToneBadge tone={conflicts.length > 0 ? 'warning' : 'none'}>
                            {conflictLabel}
                        </ToneBadge>
                    )}
                </Field>
            </div>
            <div className="mt-4 flex gap-2">
                <button
                    type="button"
                    disabled={!row.request}
                    title={!row.request ? NO_REQUEST_ACTION_TITLE : undefined}
                    onClick={() => onOpenRequest(row)}
                    className="flex-1 rounded-lg border border-primary-200 px-3 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:border-primary-300 hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-transparent disabled:text-raro-gray-300 disabled:hover:bg-transparent font-montserrat"
                >
                    {OPEN_REQUEST_LABEL}
                </button>
                {onOpenHistory && (
                    <button
                        type="button"
                        title="Ver histórico"
                        aria-label="Ver histórico de férias"
                        onClick={() =>
                            onOpenHistory({
                                id: row.collaboratorId,
                                name: row.collaboratorName,
                                email: row.collaboratorEmail,
                            })
                        }
                        className="rounded-lg border border-gray-200 px-3 py-2.5 text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
                    >
                        <ClockIcon className="h-4 w-4" />
                    </button>
                )}
            </div>
        </article>
    );
}
export default VacationDpCard;
